/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 * Tab Financial Info
 *
 * Чтобы незапутаться!
 * Всего средств на услуги - balance.totalBalance
 * Текущий баланс - balance.balance
 * Обещанные платежи - balance.virtualPayments
 * Прочие виртуальные средства - balance.financeAccums
 * Кредитный лимит - balance.creditLimit
 * так приходит с BRT :(
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Pavel Antonov
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.10.0
 */

import { Fragment, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Button, LinkBlock } from '@components/common';
import { ELinkTarget } from '@components/common/ExternalLink/ExternalLink';
import { pushGTMAnalytic } from '@modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import {
    makeSelectPageAccountInfoTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import PromisedPaymentModal from '@modules/promisedPayment/components/PromisedPaymentModal';
import { AccessComponentKeyEnum, FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import Guard from '@modules/role/Guard';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { CLIENT_ID, IRouteParams, RoutePath } from '@routes/constants/routesConstants';
import { icons } from '@theme/index';
import declination from '@utils/declination';
import { formatBalance, formatBalanceWithoutTrailingZeros, formatDate } from '@utils/formatText';
import { stringFormatParams } from '@utils/stringFormat';

import { useCheckCustomRole } from '../../../utils/useCheckCustomRole';
import ErrorPage from '../../ErrorPage/ErrorPage';
import {
    makeSelectFeatureEnabled,
    makeSelectIsFeatureWithVersionEnabled,
} from '../../PageAuth/selectors/authSelectors';
import { ECampaignsType } from '../../PageCampaign';
import { getCampaignsInternalPrefilterUrl } from '../../PageCampaign/utils/getCampaignsPageUrl';
import { accountInfoApi } from '../store';
import { IPromisedPayment } from '../types/pageAccountInfoTypes';
import {
    AccountInfoButtonHolderStyled,
    AccountInfoRowBodyStyled,
    AccountInfoRowHintStyled,
    AccountInfoRowOnTimeStyled,
    AccountInfoRowStyled,
    AccountInfoRowTitleStyled,
} from './styled/AccountInfoFinancialStyled';

const AccountInfoFinancialGeneral = () => {
    const dispatch = useDispatch();

    const { clientId } = useParams<IRouteParams>();

    const isCashbackV2Enabled = useSelector(makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.CASHBACK, [2, 3, 4]));
    const isCustomRoleCashbackMahageCheckAvailable = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_CASHBACK_MANAGE);
    const isCustomRoleDocsManageCheckAvailable = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_DOCS_MANAGE);

    const isPreliminaryDataFeatureEnabled = useSelector(
        makeSelectFeatureEnabled(FeatureNameEnum.EXPENSES_PRELIMINARY_DATA),
    );

    const translationAccountInfo = useSelector(makeSelectPageAccountInfoTranslation());
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const [
        loadFinancesMeta,
        {
            data: financesMeta,
            error: financesMetaError,
            isError: financesMetaIsError,
            isLoading: financesMetaIsLoading,
        },
    ] = accountInfoApi.endpoints.getAccountInfoFinancesMeta.useLazyQuery();

    const balancesTranslation = translationAccountInfo.financialInfo.balances;
    const urlHowToGetCashback: string = financesMeta?.loyaltyUser?.urlHowToGetCashback ?? '';

    useEffect(() => {
        loadFinancesMeta({ clientId });
    }, [clientId, loadFinancesMeta]);

    const promisedPaymentButtonHandler = useCallback(() => {
        dispatch(
            modalOpen({
                render: (modalProps: IModalRenderProps) => (
                    <PromisedPaymentModal clientId={clientId} modalId={modalProps.id} />
                ),
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const openCashbackControlLink = useCallback(() => {
        dispatch(
            pushGTMAnalytic({
                operation: GTMOperations.accountInfo.clickLinkCashbackControlAccountInfoPage(RoutePath.cashback),
                withAccountType: true,
                withSusrId: true,
                withUserRole: true,
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (financesMetaIsError) {
        const error = translationCommon.error.codes[financesMetaError?.response?.code ?? ''];
        return (
            <ErrorPage
                src={icons.errorDeliveryImage3x}
                text={error || translationCommon.error.ui.text}
                title={translationCommon.error.ui.title}
            />
        );
    }

    if (!financesMeta) return null;

    return (
        <Guard accessKey={AccessComponentKeyEnum.PageAccountInfoFinances}>
            <Loader showLoader={financesMetaIsLoading}>
                {/*Выводим неоплаченные счета для всех клиентов*/}
                {isCustomRoleDocsManageCheckAvailable && (
                    <AccountInfoRowStyled>
                        <AccountInfoRowTitleStyled>
                            {translationAccountInfo.financialInfo.unpaidBills.title}
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-unpaid-bills">
                            {financesMeta.unpaidBills?.quantity ? (
                                <Fragment>
                                    {stringFormatParams(translationAccountInfo.financialInfo.unpaidBills.unpaidAmount, {
                                        quantity: financesMeta.unpaidBills.quantity,
                                        sum: `${formatBalance(financesMeta.unpaidBills.unpaidAmount)}
                                        ${translationCommon.currency}`,
                                    })}
                                    <br />
                                    <LinkBlock url={RoutePath.billsUnpaidAccount.replace(CLIENT_ID, clientId)}>
                                        {translationAccountInfo.financialInfo.unpaidBills.more}
                                    </LinkBlock>
                                </Fragment>
                            ) : (
                                <Fragment>{translationAccountInfo.financialInfo.unpaidBills.none}</Fragment>
                            )}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                )}

                {/* Спецпредложения*/}
                {!!financesMeta.campaignsCount && (
                    <AccountInfoRowStyled data-name="account-info-finance-campaigns">
                        <AccountInfoRowTitleStyled>
                            {translationAccountInfo.financialInfo.campaigns.title}
                        </AccountInfoRowTitleStyled>
                        <LinkBlock
                            url={getCampaignsInternalPrefilterUrl(ECampaignsType.account, clientId)}
                        >
                            {financesMeta.campaignsCount}
                        </LinkBlock>
                    </AccountInfoRowStyled>
                )}
                {/*Балансы*/}

                <h3>{balancesTranslation.title}</h3>

                {/*Всего средств на услуги*/}
                {
                    <AccountInfoRowStyled>
                        <AccountInfoRowTitleStyled>
                            {balancesTranslation.totalBalance.title}
                            <AccountInfoRowHintStyled>{balancesTranslation.totalBalance.hint}</AccountInfoRowHintStyled>
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-balance-total">
                            {formatBalance(financesMeta.balance.totalBalance)} {translationCommon.currency}
                            {financesMeta.balance.refreshDate && (
                                <AccountInfoRowOnTimeStyled>
                                    {stringFormatParams(balancesTranslation.totalBalance.onTime, {
                                        date: formatDate(financesMeta.balance.refreshDate),
                                    })}
                                </AccountInfoRowOnTimeStyled>
                            )}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                }

                {/*Текущий баланс*/}
                <AccountInfoRowStyled>
                    <AccountInfoRowTitleStyled>
                        {balancesTranslation.currentBalance.title}
                        <AccountInfoRowHintStyled>{balancesTranslation.currentBalance.hint}</AccountInfoRowHintStyled>
                    </AccountInfoRowTitleStyled>
                    <AccountInfoRowBodyStyled data-name="account-info-finance-balance-current">
                        {formatBalance(financesMeta.balance.balance)} {translationCommon.currency}
                        {financesMeta.balance.refreshDate && (
                            <AccountInfoRowOnTimeStyled>
                                {stringFormatParams(balancesTranslation.currentBalance.onTime, {
                                    date: formatDate(financesMeta.balance.refreshDate),
                                })}
                            </AccountInfoRowOnTimeStyled>
                        )}
                    </AccountInfoRowBodyStyled>
                </AccountInfoRowStyled>

                {/*Обещанные платежи*/}
                {!!financesMeta.balance.virtualPayments && (
                    <AccountInfoRowStyled>
                        <AccountInfoRowTitleStyled>
                            {balancesTranslation.promisedPayments.title}
                            <AccountInfoRowHintStyled>
                                {balancesTranslation.promisedPayments.hint}
                            </AccountInfoRowHintStyled>
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-balance-virtual-payments">
                            {formatBalance(financesMeta.balance.virtualPayments)} {translationCommon.currency}
                            {financesMeta.balance.refreshDate && (
                                <AccountInfoRowOnTimeStyled>
                                    {stringFormatParams(balancesTranslation.promisedPayments.onTime, {
                                        date: formatDate(financesMeta.balance.refreshDate),
                                    })}
                                </AccountInfoRowOnTimeStyled>
                            )}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                )}

                {/*Размер кредитного лимита BRT - из financesMeta.balances.creditLimit*/}
                {!!financesMeta.balance.creditLimit && (
                    <AccountInfoRowStyled>
                        <AccountInfoRowTitleStyled>
                            {balancesTranslation.creditLimit.title}
                            <AccountInfoRowHintStyled>{balancesTranslation.creditLimit.hint}</AccountInfoRowHintStyled>
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-balance-credit-limit">
                            {financesMeta.balance.creditLimit < 9999999 ? (
                                <Fragment>
                                    {formatBalance(financesMeta.balance.creditLimit)} {translationCommon.currency}
                                </Fragment>
                            ) : (
                                <Fragment>{balancesTranslation.creditLimit.none}</Fragment>
                            )}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                )}

                {/*Другие виртуальные средства*/}
                {!!financesMeta.balance.financeAccums && (
                    <AccountInfoRowStyled>
                        <AccountInfoRowTitleStyled>
                            {balancesTranslation.virtualBalance.title}
                            <AccountInfoRowHintStyled>
                                {isCashbackV2Enabled
                                    ? balancesTranslation.virtualBalance.hint
                                    : balancesTranslation.virtualBalance.hintCashbackV1}
                            </AccountInfoRowHintStyled>
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-balance-accums">
                            {formatBalance(financesMeta.balance.financeAccums)} {translationCommon.currency}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                )}

                {/*Статус участия в ГП Кэшбэк*/}
                {financesMeta.parameters.isStableCashBack && !!financesMeta.loyaltyUser && (
                    <Fragment>
                        <h3>{translationAccountInfo.financialInfo.loyaltyUser.title}</h3>
                        <AccountInfoRowStyled>
                            <AccountInfoRowTitleStyled>
                                {translationAccountInfo.financialInfo.loyaltyUser.titleStatus}
                            </AccountInfoRowTitleStyled>
                            <AccountInfoRowBodyStyled data-name="account-info-finance-cashback">
                                {financesMeta.loyaltyUser.status.name}&nbsp;
                                {isCashbackV2Enabled && !!financesMeta.loyaltyUser?.urlHowToGetCashback && (
                                    <LinkBlock
                                        isExternalUrl
                                        className="account-financial-info__cashback-link"
                                        dataName="openHowToGetCashback"
                                        target={ELinkTarget.BLANK}
                                        url={urlHowToGetCashback}
                                    >
                                        {translationAccountInfo.financialInfo.loyaltyUser.linkGetCashback}
                                    </LinkBlock>
                                )}
                            </AccountInfoRowBodyStyled>
                        </AccountInfoRowStyled>
                        {financesMeta.loyaltyUser.bonusBalance !== null && (
                            <AccountInfoRowStyled>
                                <AccountInfoRowTitleStyled>
                                    {translationAccountInfo.financialInfo.loyaltyUser.titleBalance}
                                </AccountInfoRowTitleStyled>
                                <AccountInfoRowBodyStyled data-name="account-info-finance-balance-bonus">
                                    {formatBalanceWithoutTrailingZeros(financesMeta.loyaltyUser.bonusBalance)}&nbsp;
                                    {declination(financesMeta.loyaltyUser.bonusBalance, translationCommon.scores)}&nbsp;
                                    {isCashbackV2Enabled && isCustomRoleCashbackMahageCheckAvailable && (
                                        <LinkBlock
                                            className="account-financial-info__cashback-link"
                                            dataName="openCashBack"
                                            url={RoutePath.cashbackAccountId.replace(CLIENT_ID, clientId)}
                                            onClick={openCashbackControlLink}
                                        >
                                            {translationAccountInfo.financialInfo.loyaltyUser.linkCashbackControl}
                                        </LinkBlock>
                                    )}
                                    {financesMeta.loyaltyUser.statusDate && (
                                        <AccountInfoRowOnTimeStyled>
                                            {stringFormatParams(
                                                translationAccountInfo.financialInfo.loyaltyUser.onTime,
                                                {
                                                    date: formatDate(financesMeta.loyaltyUser.statusDate),
                                                },
                                            )}
                                        </AccountInfoRowOnTimeStyled>
                                    )}
                                </AccountInfoRowBodyStyled>
                            </AccountInfoRowStyled>
                        )}
                    </Fragment>
                )}

                {/*обещанный платеж*/}
                {(financesMeta.isPromisedPaymentButtonAvailable || financesMeta.promisedPayments?.length > 0) && (
                    <Fragment>
                        <h3>{translationAccountInfo.financialInfo.promisedPayments.title}</h3>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: translationAccountInfo.financialInfo.promisedPayments.descrHtml,
                            }}
                        />
                    </Fragment>
                )}

                {/*Кнопка подключить обещанный платеж*/}
                {financesMeta.isPromisedPaymentButtonAvailable && (
                    <Fragment>
                        <AccountInfoButtonHolderStyled>
                            <Button
                                className="widget-account__btn-promised-payment"
                                dataName="promisedPaymentButton"
                                kind="mediumNormal"
                                onClick={promisedPaymentButtonHandler}
                            >
                                {translationAccountInfo.financialInfo.promisedPayments.buttonLabel}
                            </Button>
                        </AccountInfoButtonHolderStyled>
                    </Fragment>
                )}

                {/*Обещанный платеж*/}
                {financesMeta.promisedPayments?.map((item: IPromisedPayment) => (
                    <AccountInfoRowStyled key={item.promisedPaymentId}>
                        <AccountInfoRowTitleStyled>
                            {translationAccountInfo.financialInfo.promisedPayments.titleItem}
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-promised-payments">
                            {formatBalance(item.amount)} {translationCommon.currency}
                            {item.endDate && (
                                <AccountInfoRowOnTimeStyled>
                                    {stringFormatParams(translationAccountInfo.financialInfo.promisedPayments.onTime, {
                                        date: formatDate(item.endDate),
                                    })}
                                </AccountInfoRowOnTimeStyled>
                            )}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                ))}

                {/*Прочее*/}
                <h3>{translationAccountInfo.financialInfo.otherTitle}</h3>

                {/*Баланс предыдущего периода*/}
                {financesMeta.initialBalance.value !== null && (
                    <AccountInfoRowStyled>
                        <AccountInfoRowTitleStyled>
                            {translationAccountInfo.financialInfo.initialBalance.title}
                            <AccountInfoRowHintStyled>
                                {translationAccountInfo.financialInfo.initialBalance.hint}
                            </AccountInfoRowHintStyled>
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-balance-initial">
                            {formatBalance(financesMeta.initialBalance.value)} {translationCommon.currency}
                            {financesMeta.initialBalance.date && (
                                <AccountInfoRowOnTimeStyled>
                                    {stringFormatParams(translationAccountInfo.financialInfo.initialBalance.onTime, {
                                        date: formatDate(financesMeta.initialBalance.date),
                                    })}
                                </AccountInfoRowOnTimeStyled>
                            )}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                )}

                {/*Начислено по всем абонентам с начала периода*/}
                {financesMeta.subscribersCharges.value !== null && (
                    <AccountInfoRowStyled>
                        <AccountInfoRowTitleStyled>
                            {translationAccountInfo.financialInfo.subscribersCharges.title}
                            <AccountInfoRowHintStyled>
                                {isPreliminaryDataFeatureEnabled &&
                                    translationAccountInfo.financialInfo.subscribersCharges.hint}
                            </AccountInfoRowHintStyled>
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-subscriber-charges">
                            {formatBalance(financesMeta.subscribersCharges.value)} {translationCommon.currency}
                            {financesMeta.subscribersCharges.date && (
                                <AccountInfoRowOnTimeStyled>
                                    {stringFormatParams(
                                        translationAccountInfo.financialInfo.subscribersCharges.onTime,
                                        {
                                            date: formatDate(financesMeta.subscribersCharges.date),
                                        },
                                    )}
                                </AccountInfoRowOnTimeStyled>
                            )}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                )}

                {/*Платежи с начала периода*/}
                {financesMeta.accountPayments.value !== null && (
                    <AccountInfoRowStyled>
                        <AccountInfoRowTitleStyled>
                            {translationAccountInfo.financialInfo.accountPayments.title}
                            <AccountInfoRowHintStyled>
                                {translationAccountInfo.financialInfo.accountPayments.hint}
                            </AccountInfoRowHintStyled>
                        </AccountInfoRowTitleStyled>
                        <AccountInfoRowBodyStyled data-name="account-info-finance-payments">
                            {formatBalance(financesMeta.accountPayments.value)} {translationCommon.currency}
                            {financesMeta.accountPayments.date && (
                                <AccountInfoRowOnTimeStyled>
                                    {stringFormatParams(translationAccountInfo.financialInfo.accountPayments.onTime, {
                                        date: formatDate(financesMeta.accountPayments.date),
                                    })}
                                </AccountInfoRowOnTimeStyled>
                            )}
                        </AccountInfoRowBodyStyled>
                    </AccountInfoRowStyled>
                )}
            </Loader>
        </Guard>
    );
};

export default AccountInfoFinancialGeneral;
